import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout';
import { Card } from '../components/ui';
import useThemeStore from '../stores/themeStore';

interface SettingOption {
	icon: string;
	title: string;
	description: string;
	path?: string;
	comingSoon?: boolean;
}

const Settings = () => {
	const navigate = useNavigate();
	const { theme, setTheme, resolvedTheme } = useThemeStore();

	const settingsOptions: SettingOption[] = [
		{
			icon: '📍',
			title: 'Locations',
			description: 'Manage your business locations',
			path: '/locations',
		},
		{
			icon: '👥',
			title: 'Roles',
			description: 'Create and manage user roles',
			path: '/roles',
		},
		{
			icon: '👤',
			title: 'User Management',
			description: 'Manage users and permissions',
			comingSoon: true,
		},
		{
			icon: '💳',
			title: 'Payment Settings',
			description: 'Configure payment methods and preferences',
			comingSoon: true,
		},
		{
			icon: '📧',
			title: 'Notifications',
			description: 'Email and SMS notification preferences',
			comingSoon: true,
		},
		{
			icon: '🔒',
			title: 'Security',
			description: 'Password and security settings',
			comingSoon: true,
		},
	];

	const handleOptionClick = (option: SettingOption) => {
		if (option.path) {
			navigate(option.path);
		} else if (option.comingSoon) {
			// Could show a toast here
			console.log('Coming soon:', option.title);
		}
	};

	return (
		<AppLayout>
			<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Settings</h1>

			{/* Appearance Settings Card */}
			<Card className="mb-6">
				<h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 md:mb-6">Appearance</h2>

				{/* Theme Toggle */}
				<div className="bg-white dark:bg-gray-800 p-4 md:p-5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-4">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
						<div className="flex-1">
							<h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
								{resolvedTheme === 'dark' ? '🌙' : '☀️'} Theme
							</h3>
							<p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
								Choose your preferred color scheme
							</p>
						</div>
						<div className="flex gap-2 w-full sm:w-auto">
							<button
								onClick={() => setTheme('light')}
								className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
									theme === 'light'
										? 'bg-blue-600 text-white shadow-md'
										: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								☀️ Light
							</button>
							<button
								onClick={() => setTheme('dark')}
								className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
									theme === 'dark'
										? 'bg-blue-600 text-white shadow-md'
										: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								🌙 Dark
							</button>
							<button
								onClick={() => setTheme('system')}
								className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
									theme === 'system'
										? 'bg-blue-600 text-white shadow-md'
										: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								💻 System
							</button>
						</div>
					</div>
					{theme === 'system' && (
						<div className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded px-3 py-2">
							ℹ️ Following system preference: Currently using {resolvedTheme} mode
						</div>
					)}
				</div>
			</Card>

			<Card>
				<h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 md:mb-6">General Settings</h2>

				<div className="space-y-3 md:space-y-4">
					{settingsOptions.map((option, index) => (
						<div
							key={index}
							className={`bg-white dark:bg-gray-800 p-4 md:p-5 rounded-lg shadow-sm transition-all border border-gray-200 dark:border-gray-700 ${
								option.comingSoon
									? 'cursor-default opacity-70'
									: 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-blue-400 dark:hover:border-blue-600'
							}`}
							onClick={() => handleOptionClick(option)}
						>
							<div className="flex items-center justify-between gap-3">
								<div className="flex-1 min-w-0">
									<h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-1 flex flex-wrap items-center gap-2">
										<span>{option.icon} {option.title}</span>
										{option.comingSoon && (
											<span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded whitespace-nowrap">
												Coming Soon
											</span>
										)}
									</h3>
									<p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{option.description}</p>
								</div>
								{!option.comingSoon && <div className="text-xl sm:text-2xl text-gray-400 dark:text-gray-500 flex-shrink-0">›</div>}
							</div>
						</div>
					))}
				</div>
			</Card>

			<div className="mt-6 text-sm text-gray-500 dark:text-gray-400 text-center">© Beauty Business Portal</div>
		</AppLayout>
	);
};

export default Settings;
